import InfoCard from 'components/ui/InfoCard'
import {Link, useNavigate, useParams} from 'react-router-dom'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import {Typography} from '@mui/material'
import Button from 'components/ui/Button'
import * as Styles from 'layouts/PrivateLayout.styles'
import BookingDetailsOrderData from './bookingDetails/OrderData'
import BookingDetailsCustomerData from './bookingDetails/CustomerData'
import BookingData from './bookingDetails/BookingData'
import {useGetBookingSlotQuery} from 'services/bookingSlots'
import {formatDateTime} from 'utils/intlDate'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import {useState} from 'react'
import {useUpdateOrderNotificationStatusMutation} from 'services/orders'
import {enqueueSnackbar} from 'notistack'
import CancelBooking from "../../../components/ui/CancelBooking";
import BookingNoteEditor from "../../../components/ui/BookingNoteEditor";

const BookingDetails = () => {
  const { orderId = '', bookingId = '' } = useParams()
  const {
    data: bookingSlot,
    isFetching,
    isLoading,
    refetch: refetchBookingSlot
  } = useGetBookingSlotQuery(bookingId)
  const bookingCreatedAt = formatDateTime(
    new Date(bookingSlot?.booking_slot?.created_at ?? new Date())
  )

  const [updateOrderNotification] = useUpdateOrderNotificationStatusMutation()
  const [toggleSmsEmail, setToggleSmsEmail] = useState<string>('')
  const [openConfirm, setOpenConfirm] = useState<boolean>(false)
  const [resetNotification, setResetNotification] = useState<boolean>(false)

  const handleNavigateToReschedule = () => {
    if (bookingSlot) {
      navigate(
        `/admin/orders/${bookingSlot.booking_slot.order.id}/booking-details/${bookingSlot.booking_slot.id}/edit`,
        { state: { locations: bookingSlot.booking_slot.location_id } }
      )
    }
  }

  const handleOpenConfirm = (type: string) => {
    setToggleSmsEmail(type)
    setOpenConfirm(true)
  }

  const handleCloseConfirm = () => {
    setOpenConfirm(false)
  }

  const handleToggleSmsOrEmail = async () => {
    setResetNotification(true)
    let dataToggle24Hours = {}
    let dataToggle1Hour = null

    if (bookingSlot) {
      if (toggleSmsEmail === 'email') {
        dataToggle24Hours = {
          type: 'email_24_hr',
          value: 0
        }
      } else {
        dataToggle24Hours = {
          type: 'sms_24_hr',
          value: 0
        }
        dataToggle1Hour = {
          type: 'sms_1_hr',
          value: 0
        }
      }
    }

    try {
      const data = {
        orderId,
        body: dataToggle24Hours
      }

      await updateOrderNotification(data)

      if (dataToggle1Hour !== null) {
        const data = {
          orderId,
          body: dataToggle1Hour
        }
        await updateOrderNotification(data)
      }

      enqueueSnackbar(`${toggleSmsEmail} notifications successfully reset`, {
        variant: 'success', autoHideDuration: 1000
      })

      await refetchBookingSlot()
    } catch (error) {
      enqueueSnackbar(
        `Update ${toggleSmsEmail} notification failed. Please try again`,
        {
          variant: 'error', autoHideDuration: 1000
        }
      )
    }
    setOpenConfirm(false)
    setResetNotification(false)
  }

  const bookingOrderData = <BookingDetailsOrderData booking={bookingSlot?.booking_slot} />
  const customerData = <BookingDetailsCustomerData booking={bookingSlot?.booking_slot} />
  const bookingData = <BookingData orderId={orderId} booking={bookingSlot?.booking_slot} />
  const navigate = useNavigate()

  const navigateBackToShopify = () => {
    window.open(`${process.env.REACT_APP_SHOPIFY_URL}/orders/${bookingSlot?.booking_slot.order.order_id}/refund`, '_blank', 'noopener,noreferrer');
  }

  return (
      <>
        <Styles.FlexContainer flex-row-lg className="pb-4">
          <Styles.FlexContainerColumn>
            <Styles.FlexContainer>
              <Styles.FlexContainerColumn>
                <Link to={`/admin/orders/${orderId}`}>
                  <ArrowBackIcon sx={{fontSize: 24}}/>
                </Link>
              </Styles.FlexContainerColumn>
              <Styles.FlexContainerColumn className="ml-4">
                <Typography sx={{fontSize: 18}}>Booking Details</Typography>
                <Typography
                    sx={{fontSize: 14}}
                    color="text.secondary"
                    gutterBottom
                >
                  Registered: {bookingCreatedAt}
                </Typography>
              </Styles.FlexContainerColumn>
            </Styles.FlexContainer>
          </Styles.FlexContainerColumn>
          <Styles.FlexContainerColumn push-right-lg>
            <Styles.PageTitleContainerButtons>
              <CancelBooking currentBooking={bookingSlot?.booking_slot?.id ? bookingSlot?.booking_slot?.id : ""}/>
              <Button onClick={navigateBackToShopify}>Refund Order</Button>
              <Button onClick={() => handleOpenConfirm('sms')}>Send SMS</Button>
              <Button onClick={() => handleOpenConfirm('email')}>
                Send Email
              </Button>
              <Button cc-color="purple" onClick={handleNavigateToReschedule}>
                Reschedule
              </Button>
            </Styles.PageTitleContainerButtons>
          </Styles.FlexContainerColumn>
        </Styles.FlexContainer>
        <div className="my-6"></div>
        <InfoCard
            label="Class Information"
            isLoading={false}
            value={bookingData}
        />
        <div className="my-6"></div>

        <InfoCard label="Customer" isLoading={false} value={customerData}/>
        <div className="my-6"></div>
        <InfoCard
            label="Order Details"
            isLoading={false}
            value={bookingOrderData}
        />
        <div className="my-6"></div>
        <InfoCard
            label={'Notes'}
            value={
              <BookingNoteEditor
                  bookingSlotId={bookingId}
                  initialNote={bookingSlot?.booking_slot?.note || ''}
              />
            }
            isLoading={isLoading}
        />
        <div className="my-6"></div>
        <Dialog
            open={openConfirm}
            onClose={handleCloseConfirm}
            aria-labelledby="Delete Additional information"
        >
          <DialogTitle id={`alert-dialog-title`}>{'Are you sure?'}</DialogTitle>
          <DialogContent>
            <DialogContentText id={`alert-dialog-description`}>
              Are you sure you want to reset {toggleSmsEmail} notifications?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseConfirm}>Cancel</Button>
            <Button
                cc-color="purple"
                onClick={handleToggleSmsOrEmail}
                disabled={resetNotification}
            >
              Send
            </Button>
          </DialogActions>
        </Dialog>
      </>
  )
}

export default BookingDetails
